import React from 'react';
import { Container, Paper, Title, Text, Box, Stack } from '@mantine/core';
import { MarketingHeader } from '../../components/layout/MarketingHeader';
import { Footer } from '../../components/layout/Footer';

/**
 * AuthLayout Props
 */
export interface AuthLayoutProps {
  /** Page title (e.g., "Welcome Back", "Create Account") */
  title: string;
  /** Optional subtitle/description */
  subtitle?: React.ReactNode;
  /** Form/content to display */
  children: React.ReactNode;
  /** Optional max width for the container (default: 'sm') */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Show marketing header above the form (for registration page) */
  showMarketingHeader?: boolean;
}

/**
 * AuthLayout - Reusable layout for authentication pages
 *
 * Features:
 * - Centered card design with consistent styling
 * - MentionedMe branding with Comfortaa font
 * - Responsive layout
 * - Used by LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage
 */
export const AuthLayout: React.FC<AuthLayoutProps> = ({
  title,
  subtitle,
  children,
  maxWidth = 'sm',
  showMarketingHeader = false
}): React.ReactElement => {
  return (
    <Stack justify="space-between" style={{ minHeight: '100vh' }}>
      <Box>
        {showMarketingHeader && <MarketingHeader />}
        <Container size={maxWidth} mt={showMarketingHeader ? 'md' : 'xl'} pt="xl" pb="xl">
          <Paper shadow="md" radius="md" p="xl" withBorder>
            <Stack gap="lg">
              {/* Header with branding */}
              <Box>
                <Title order={2} ta="center" ff="Comfortaa, sans-serif">
                  {title}
                </Title>
                {subtitle && (
                  <Text c="dimmed" ta="center" mt="xs">
                    {subtitle}
                  </Text>
                )}
              </Box>

              {/* Content */}
              {children}
            </Stack>
          </Paper>
        </Container>
      </Box>

      {/* Footer with reCAPTCHA attribution */}
      <Footer />
    </Stack>
  );
};
